import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';

const LegalPage = ({ titleEn, titleAr, lastUpdatedEn, lastUpdatedAr, seo, children }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const title = isAr ? titleAr : titleEn;
  const lastUpdated = isAr ? lastUpdatedAr : lastUpdatedEn;
  return (
    <>
      {seo && (
        <SEO
          titleEn={seo.titleEn}
          titleAr={seo.titleAr}
          descEn={seo.descEn}
          descAr={seo.descAr}
          path={seo.path}
        />
      )}
      <div className="pt-16">
        <section className="py-20 lg:py-24 bg-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground mb-2">{title}</h1>
              <p className="text-sm text-muted-foreground">{lastUpdated}</p>
            </div>
            <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
              {children}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-base font-semibold text-foreground mt-8 mb-3">{title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const PrivacyPage = () => (
  <LegalPage
    titleEn="Privacy Policy"
    titleAr="سياسة الخصوصية"
    lastUpdatedEn="Last updated: January 1, 2025"
    lastUpdatedAr="آخر تحديث: ١ يناير ٢٠٢٥"
    seo={{
      titleEn: 'Privacy Policy | Quidory',
      titleAr: 'سياسة الخصوصية | كويدوري',
      descEn: 'Learn how Quidory collects, uses, and protects your personal data. Our commitment to your privacy and data security.',
      descAr: 'تعرف على كيفية جمع كويدوري لبياناتك الشخصية واستخدامها وحمايتها. التزامنا بخصوصيتك وأمان بياناتك.',
      path: '/privacy',
    }}
  >
    <Section title="1. Introduction">
      <p>Quidory ("we," "us," or "our"), a service operated by <strong className="text-foreground">Al Yakhour LLC</strong> (registered in Wyoming, USA), provides an AI-powered multilingual intelligence and publishing platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.</p>
      <p>By using Quidory, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the platform.</p>
    </Section>

    <Section title="2. Information We Collect">
      <p><strong className="text-foreground">Account Information:</strong> When you register, we collect your name, email address, and password (encrypted).</p>
      <p><strong className="text-foreground">Usage Data:</strong> We automatically collect information about how you interact with our platform, including the pages you visit, features you use, and the time and duration of your visits.</p>
      <p><strong className="text-foreground">Content Data:</strong> Content you create, schedule, or publish through our platform is stored to provide the service.</p>
      <p><strong className="text-foreground">Communication Data:</strong> Messages and requests you send to our support team.</p>
      <p><strong className="text-foreground">Technical Data:</strong> IP address, browser type, device information, and cookies.</p>
    </Section>

    <Section title="3. How We Use Your Information">
      <p>We use the collected information for the following purposes:</p>
      <ul className="list-disc ps-5 space-y-1.5 mt-2">
        <li>To provide, operate, and maintain our platform and services</li>
        <li>To improve, personalize, and expand our services</li>
        <li>To understand and analyze how you use our platform</li>
        <li>To develop new products, services, and features</li>
        <li>To communicate with you about updates, security alerts, and support</li>
        <li>To prevent fraudulent transactions and monitor against theft</li>
        <li>To comply with legal obligations</li>
      </ul>
    </Section>

    <Section title="4. Data Sharing and Disclosure">
      <p>We do not sell, trade, or rent your personal information to third parties. We may share information in the following limited circumstances:</p>
      <ul className="list-disc ps-5 space-y-1.5 mt-2">
        <li><strong className="text-foreground">Service Providers:</strong> Trusted third-party vendors who assist us in operating the platform (e.g., cloud hosting, analytics)</li>
        <li><strong className="text-foreground">Legal Requirements:</strong> When required by law or to protect our rights</li>
        <li><strong className="text-foreground">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
        <li><strong className="text-foreground">With Your Consent:</strong> For any other purpose with your explicit consent</li>
      </ul>
    </Section>

    <Section title="5. Data Retention">
      <p>We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy, or as required by law. Account data is retained for the duration of your account plus 90 days after deletion.</p>
    </Section>

    <Section title="6. Your Rights">
      <p>Depending on your location, you may have the following rights regarding your personal data:</p>
      <ul className="list-disc ps-5 space-y-1.5 mt-2">
        <li><strong className="text-foreground">Access:</strong> Request a copy of the personal data we hold about you</li>
        <li><strong className="text-foreground">Correction:</strong> Request correction of inaccurate or incomplete data</li>
        <li><strong className="text-foreground">Deletion:</strong> Request deletion of your personal data</li>
        <li><strong className="text-foreground">Portability:</strong> Request a portable copy of your data</li>
        <li><strong className="text-foreground">Objection:</strong> Object to processing of your personal data</li>
        <li><strong className="text-foreground">Withdrawal:</strong> Withdraw consent at any time</li>
      </ul>
      <p>To exercise these rights, please contact us at <strong className="text-foreground">support@quidory.com</strong>.</p>
    </Section>

    <Section title="7. Security">
      <p>We implement appropriate technical and organizational security measures to protect your personal information against accidental or unlawful destruction, loss, alteration, unauthorized disclosure, or access. However, no method of transmission over the Internet is 100% secure.</p>
    </Section>

    <Section title="8. Cookies">
      <p>We use cookies and similar tracking technologies to track activity on our platform and store certain information. Please see our Cookie Policy for more details.</p>
    </Section>

    <Section title="9. Children's Privacy">
      <p>Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you become aware that a child has provided us with personal information, please contact us.</p>
    </Section>

    <Section title="10. Changes to This Policy">
      <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>
    </Section>

    <Section title="11. Contact Us">
      <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
      <p>
        <strong className="text-foreground">Email:</strong> support@quidory.com<br />
        <strong className="text-foreground">Website:</strong> https://quidory.com<br />
        <strong className="text-foreground">Legal Entity:</strong> Al Yakhour LLC<br />
        <strong className="text-foreground">Registered Office:</strong> Wyoming, USA
      </p>
    </Section>
  </LegalPage>
);

export default PrivacyPage;
